import Papa from 'papaparse'

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Clean currency strings: strip £, $, €, commas, spaces, corrupt prefix chars
function parseMoney(str) {
  if (!str && str !== 0) return null
  const cleaned = String(str).replace(/[^0-9.\-]/g, '')
  const val = parseFloat(cleaned)
  return isNaN(val) ? null : val
}

// Combine a "money in" and "money out" column into a signed amount.
// Positive = money in, negative = money out.
function combineInOut(inVal, outVal) {
  const inAmt = parseMoney(inVal)
  const outAmt = parseMoney(outVal)
  if (inAmt) return inAmt
  if (outAmt) return -Math.abs(outAmt)
  return null
}

// Normalise a header string for matching
const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '').trim()

// Find the first row index where a cell matches any of the candidate header names
// Find the row index that looks like the header row for a specific export
// format. The match is strict: every name in `candidates` must appear in
// the same row. This is intentional and important. The previous looser
// rule (any 2 candidates match) let bank-statement CSVs with columns like
// "Date, Description, Paid Out, Paid In" be misidentified as Sage (which
// has "Date, Description, Money In, Money Out") just because Date and
// Description overlap. The strict version refuses to parse a CSV with the
// "wrong" column names, falling through to the custom mapper instead.
//
// Returns -1 if no row in the first 10 contains all required candidates.
// Callers should bail out when -1 is returned.
function findHeaderRow(rows, candidates) {
  const normCandidates = candidates.map(norm)
  for (let i = 0; i < Math.min(rows.length, 10); i++) {
    const normRow = rows[i].map(norm)
    if (normCandidates.every(c => normRow.includes(c))) return i
  }
  return -1
}

// Format a date string to YYYY-MM-DD for storage
function normaliseDate(str) {
  if (!str) return ''
  str = str.trim()

  // Already ISO: 2026-03-20
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str

  // DD/MM/YYYY or DD/MM/YY
  const dmyMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/)
  if (dmyMatch) {
    let [, d, m, y] = dmyMatch
    if (y.length === 2) y = '20' + y
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
  }

  return str
}

// Strip FreeAgent's ///£82.00 suffix from description
function cleanFreeAgentDescription(str) {
  if (!str) return ''
  return str.replace(/\/\/\/.*$/, '').trim()
}

// ─── Format-specific parsers ──────────────────────────────────────────────────

function parseFreeAgent(rows) {
  // 3 metadata rows at top before real headers (Report title, Account name, Date range)
  // Real headers: Date, Description, Type, Money in, Money out
  const headerRowIdx = findHeaderRow(rows, ['Date', 'Description', 'Money in', 'Money out', 'Type'])
  if (headerRowIdx === -1) return []
  const dataRows = rows.slice(headerRowIdx + 1)

  return dataRows
    .filter(r => r[0] && r[0].trim() && r[0].trim().toLowerCase() !== 'date')
    .map(r => ({
      date: normaliseDate(r[0]),
      description: cleanFreeAgentDescription(r[1]),
      amount: combineInOut(r[3], r[4]),  // Money in (col 3), Money out (col 4)
      payee: '',                         // FreeAgent doesn't provide a payee column
      reference: '',
      category: (r[2] || '').trim(),     // Type column
    }))
    .filter(r => r.description)
}

function parseQuickBooks(rows) {
  // Tab-delimited
  // Headers: Date, Bank description, Spent, Received, VAT, From/To, Match/Categorise
  const headerRowIdx = findHeaderRow(rows, ['Date', 'Bank description', 'Spent', 'Received'])
  if (headerRowIdx === -1) return []
  const headerRow = rows[headerRowIdx].map(norm)

  const colIdx = (names) => {
    for (const name of names) {
      const idx = headerRow.indexOf(norm(name))
      if (idx !== -1) return idx
    }
    return -1
  }

  const dateCol      = colIdx(['Date'])
  const descCol      = colIdx(['Bank description', 'Description'])
  const spentCol     = colIdx(['Spent'])
  const receivedCol  = colIdx(['Received'])
  const payeeCol     = colIdx(['From/To'])
  const categoryCol  = colIdx(['Match/Categorise'])

  return rows.slice(headerRowIdx + 1)
    .filter(r => r[dateCol] && r[dateCol].trim())
    .map(r => ({
      date:        normaliseDate(r[dateCol]),
      description: (r[descCol] || '').trim(),
      // Received = money coming in (positive), Spent = money going out (negative)
      amount:      combineInOut(
                     receivedCol !== -1 ? r[receivedCol] : null,
                     spentCol    !== -1 ? r[spentCol]    : null
                   ),
      payee:       (payeeCol    !== -1 ? r[payeeCol]    : '').trim(),
      reference:   '',
      category:    (categoryCol !== -1 ? r[categoryCol] : '').trim(),
    }))
    .filter(r => r.description)
}

function parseSage(rows) {
  // Clean format, no metadata rows
  // Headers: Date, Description, Money In, Money Out
  const headerRowIdx = findHeaderRow(rows, ['Date', 'Description', 'Money In', 'Money Out'])
  if (headerRowIdx === -1) return []
  const dataRows = rows.slice(headerRowIdx + 1)

  return dataRows
    .filter(r => r[0] && r[0].trim())
    .map(r => ({
      date:        normaliseDate(r[0]),
      description: (r[1] || '').trim(),
      amount:      combineInOut(r[2], r[3]),  // Money In (col 2), Money Out (col 3)
      payee:       '',                        // Sage doesn't provide a payee column
      reference:   '',
      category:    '',
    }))
    .filter(r => r.description)
}

function parseXero(rows) {
  // 2 metadata rows: Account name, Account number
  // Headers: Date, Payee, Particulars | Reference | Code, Spent, Received, Tax, Your Comments
  const headerRowIdx = findHeaderRow(rows, ['Date', 'Payee', 'Spent', 'Received'])
  if (headerRowIdx === -1) return []
  const headerRow = rows[headerRowIdx]
  const headerRowNorm = headerRow.map(norm)

  const colIdx = (names) => {
    for (const name of names) {
      const n = norm(name)
      const idx = headerRowNorm.findIndex(h => h.includes(n) || n.includes(h))
      if (idx !== -1) return idx
    }
    return -1
  }

  const dateCol        = colIdx(['Date'])
  const payeeCol       = colIdx(['Payee'])
  const particularsCol = headerRowNorm.findIndex(h => h.includes('particulars') || h.includes('reference'))
  const spentCol       = colIdx(['Spent'])
  const receivedCol    = colIdx(['Received'])

  return rows.slice(headerRowIdx + 1)
    .filter(r => r.length > 2 && r[dateCol] && r[dateCol].trim())
    .map(r => {
      // Particulars column format: "Particulars | Reference | Code"
      const particularsRaw = particularsCol !== -1 ? (r[particularsCol] || '') : ''
      const parts = particularsRaw.split('|').map(p => p.trim())
      const particulars = parts[0] || ''
      const reference   = parts[1] || ''

      // Do NOT use the Payee column for the description — it is an unconfirmed
      // guess by Xero/QuickBooks. Use the human-readable reference (pipe segment 2)
      // or fall back to particulars (pipe segment 1).
      const description = reference || particulars

      // Capture payee for the filename only — even if it's wrong, the bookkeeper
      // can rename. The description above (which is what shows on screen) stays
      // accurate either way.
      const payee = payeeCol !== -1 ? (r[payeeCol] || '').trim() : ''

      return {
        date:        normaliseDate(r[dateCol]),
        description,
        amount:      combineInOut(
                       receivedCol !== -1 ? r[receivedCol] : null,
                       spentCol    !== -1 ? r[spentCol]    : null
                     ),
        payee,
        reference,
        category:    '',
      }
    })
    .filter(r => r.description)
}

// ─── Auto-detection ───────────────────────────────────────────────────────────

function detectSoftware(rawText, rows) {
  const firstLine = rawText.split('\n')[0] || ''

  // QuickBooks: tab-delimited with "Bank description" header
  if (firstLine.includes('\t')) return 'quickbooks'

  // Look through first few rows for identifying headers
  const topRows = rows.slice(0, 6)
  const allText = topRows.map(r => r.join(' ')).join(' ').toLowerCase()

  // Xero: has "Payee" and "Particulars" or "Spent"/"Received" with ISO dates
  if (allText.includes('payee') || allText.includes('particulars')) return 'xero'

  // FreeAgent: metadata rows at top mentioning "Bank Reconciliation" or "Accounting Year"
  const firstFewCells = topRows.map(r => (r[0] || '').toLowerCase()).join(' ')
  if (
    firstFewCells.includes('bank reconciliation') ||
    firstFewCells.includes('accounting year') ||
    firstFewCells.includes('full report')
  ) return 'freeagent'

  // Sage: simple Date, Description, Money In, Money Out — no metadata rows
  // This is the safest fallback since it's the cleanest format
  return 'sage'
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function parseCSV(file, softwareHint = 'auto') {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const rawText = e.target.result

        // Detect delimiter (QuickBooks uses tabs)
        const firstLine = rawText.split('\n')[0] || ''
        const delimiter = firstLine.includes('\t') ? '\t' : ','

        // Parse all rows as arrays (we handle headers ourselves per format)
        const result = Papa.parse(rawText, {
          delimiter,
          skipEmptyLines: true,
          header: false,
        })

        const rows = result.data

        if (!rows || rows.length < 2) {
          reject(new Error('File appears to be empty or has only one row.'))
          return
        }

        // Detect or use hinted software
        const software = softwareHint !== 'auto'
          ? softwareHint
          : detectSoftware(rawText, rows)

        // Route to the correct parser
        let transactions = []
        switch (software) {
          case 'freeagent':  transactions = parseFreeAgent(rows);  break
          case 'quickbooks': transactions = parseQuickBooks(rows); break
          case 'sage':       transactions = parseSage(rows);       break
          case 'xero':       transactions = parseXero(rows);       break
          default:           transactions = parseSage(rows)
        }

        transactions = transactions.filter(t => t.description && t.description.length > 0)

        if (transactions.length === 0) {
          // Auto-detect failed to extract anything. Return the raw rows so
          // the caller can fall back to the custom mapper instead of just
          // showing an error. The caller decides whether to show the
          // mapper or surface the failure.
          resolve({ transactions: [], software, rowCount: 0, rawRows: rows })
          return
        }

        resolve({ transactions, software, rowCount: transactions.length, rawRows: rows })
      } catch (err) {
        reject(new Error('Could not parse file: ' + err.message))
      }
    }

    reader.onerror = () => reject(new Error('Could not read the file.'))
    reader.readAsText(file, 'utf-8')
  })
}

// ─── Raw CSV reader (for custom mapping) ─────────────────────────────────────
//
// Reads a CSV file into rows without trying to detect the format. Used by
// the custom column mapper, which then asks the user how to interpret the
// rows. The returned rows are arrays of strings, one per cell.

export function parseRawCsv(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const rawText = e.target.result
        const firstLine = rawText.split('\n')[0] || ''
        const delimiter = firstLine.includes('\t') ? '\t' : ','
        const result = Papa.parse(rawText, {
          delimiter,
          skipEmptyLines: true,
          header: false,
        })
        const rows = result.data
        if (!rows || rows.length === 0) {
          reject(new Error('File appears to be empty.'))
          return
        }
        resolve({ rows, delimiter })
      } catch (err) {
        reject(new Error('Could not read CSV: ' + err.message))
      }
    }
    reader.onerror = () => reject(new Error('Could not read the file.'))
    reader.readAsText(file, 'utf-8')
  })
}

// ─── Custom mapping ─────────────────────────────────────────────────────────
//
// Applies a user-defined column mapping to raw rows to produce transactions.
// The mapping describes which CSV columns hold the date, description, amount
// etc., plus a header row index so metadata rows above the headers can be
// skipped.
//
// Mapping shape (all integers are 0-based column indices; -1 means "not mapped"):
//   {
//     headerRowIndex: number,       // row to treat as headers; data starts at +1
//     dateColumn:     number,
//     descriptionColumn: number,
//     payeeColumn:    number,       // -1 if not mapped
//     amountMode:     'single' | 'split',
//     amountColumn:   number,       // used when amountMode === 'single'
//     moneyInColumn:  number,       // used when amountMode === 'split'
//     moneyOutColumn: number,       // used when amountMode === 'split'
//     dateFormat:     'auto' | 'DMY' | 'MDY' | 'YMD',
//     reverseSign:    boolean,      // flip all amounts after parsing.
//                                   // Useful for credit card statements where
//                                   // expenditure is shown as positive numbers.
//   }
//
// Returns an array of transaction objects matching what the format-specific
// parsers return: { date, description, amount, payee, reference, category }.
// Rows where description is empty or amount is null are dropped.

export function parseWithMapping(rows, mapping) {
  if (!rows || rows.length === 0) return []

  const startIdx = (mapping.headerRowIndex ?? 0) + 1
  const dataRows = rows.slice(startIdx)
  const reverse = !!mapping.reverseSign

  return dataRows
    .map(r => {
      const dateRaw   = pickCell(r, mapping.dateColumn)
      const descRaw   = pickCell(r, mapping.descriptionColumn)
      const payeeRaw  = pickCell(r, mapping.payeeColumn)

      // Amount: either single column (with sign) or split in/out
      let amount = null
      if (mapping.amountMode === 'split') {
        const inAmt  = parseMoney(pickCell(r, mapping.moneyInColumn))
        const outAmt = parseMoney(pickCell(r, mapping.moneyOutColumn))
        if (inAmt)       amount = inAmt
        else if (outAmt) amount = -Math.abs(outAmt)
      } else {
        amount = parseMoney(pickCell(r, mapping.amountColumn))
      }

      // Reverse sign if the source treats expenditure as positive (typical
      // for credit card statements). Applied AFTER the in/out split logic
      // so it works uniformly across both modes.
      if (reverse && amount !== null) amount = -amount

      return {
        date:        normaliseDateWithFormat(dateRaw, mapping.dateFormat || 'auto'),
        description: (descRaw || '').trim(),
        amount,
        payee:       (payeeRaw || '').trim(),
        reference:   '',
        category:    '',
      }
    })
    // Drop rows where we couldn't get either a description or an amount.
    // An all-blank row in the middle of the data shouldn't crash the import.
    .filter(t => t.description && t.amount !== null && !isNaN(new Date(t.date).getTime()))
}

// Safe cell lookup: returns '' if the column index is -1 or the row is short
function pickCell(row, colIdx) {
  if (colIdx == null || colIdx < 0) return ''
  if (!row || colIdx >= row.length) return ''
  return row[colIdx]
}

// Like normaliseDate but takes an explicit format. 'auto' falls back to the
// original heuristics used by normaliseDate. Supports DMY (DD/MM/YYYY),
// MDY (MM/DD/YYYY), and YMD (YYYY-MM-DD).
function normaliseDateWithFormat(str, format) {
  if (!str) return ''
  str = String(str).trim()

  // ISO is always recognised since it's unambiguous
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str

  // Slash- or dash-separated three-part dates
  const match = str.match(/^(\d{1,4})[\/\-.](\d{1,4})[\/\-.](\d{1,4})$/)
  if (match) {
    let [, a, b, c] = match
    let d, m, y
    if (format === 'YMD' || (format === 'auto' && a.length === 4)) {
      y = a; m = b; d = c
    } else if (format === 'MDY') {
      m = a; d = b; y = c
    } else {
      // DMY default / auto fallback
      d = a; m = b; y = c
    }
    if (y.length === 2) y = '20' + y
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
  }

  return str // give up; downstream filter will drop unparseable rows
}

// ─── Mapping auto-suggestion ─────────────────────────────────────────────────
//
// Inspects the header row and proposes a best-guess mapping. The user can
// override anything. Saves them clicking through every dropdown when the
// CSV has reasonably named columns.

export function suggestMapping(rows) {
  // Detect the most likely header row (look for one with multiple text cells
  // and no obvious numeric data). Cap search at first 10 rows.
  let headerRowIndex = 0
  for (let i = 0; i < Math.min(rows.length, 10); i++) {
    const cells = rows[i].map(c => (c || '').trim())
    const nonEmpty = cells.filter(c => c.length > 0)
    if (nonEmpty.length < 2) continue
    // A header row usually has no cells that parse as money or look like dates
    const looksLikeData = nonEmpty.some(c =>
      /^[\-£$]?\d/.test(c) || /\d{1,2}[\/\-]\d{1,2}/.test(c)
    )
    if (!looksLikeData) {
      headerRowIndex = i
      break
    }
  }

  const headers = (rows[headerRowIndex] || []).map(norm)
  // Two-pass match: first look for an exact match (preferred), then fall back
  // to substring matches. This avoids picking "Merchant Currency Description"
  // when there's a column called just "Merchant" you'd rather use.
  const findCol = (patterns) => {
    for (const p of patterns) {
      const target = norm(p)
      const idx = headers.findIndex(h => h === target)
      if (idx !== -1) return idx
    }
    for (const p of patterns) {
      const target = norm(p)
      const idx = headers.findIndex(h => h && h.includes(target))
      if (idx !== -1) return idx
    }
    return -1
  }

  const dateColumn        = findCol(['date', 'transactiondate', 'posted'])
  const descriptionColumn = findCol(['description', 'narrative', 'details', 'memo', 'bankdescription', 'reference'])
  const payeeColumn       = findCol(['payee', 'fromto', 'merchant', 'counterparty'])
  const moneyInColumn     = findCol(['moneyin', 'received', 'credit', 'in', 'deposit'])
  const moneyOutColumn    = findCol(['moneyout', 'spent', 'debit', 'out', 'withdrawal'])
  const singleAmountCol   = findCol(['amount', 'value', 'total'])

  // Decide single vs split: if we found both money-in and money-out, use split
  const hasSplit = moneyInColumn !== -1 && moneyOutColumn !== -1

  return {
    headerRowIndex,
    dateColumn,
    descriptionColumn,
    payeeColumn,
    amountMode: hasSplit ? 'split' : 'single',
    amountColumn:   hasSplit ? -1 : singleAmountCol,
    moneyInColumn:  hasSplit ? moneyInColumn  : -1,
    moneyOutColumn: hasSplit ? moneyOutColumn : -1,
    dateFormat:     'auto',
    reverseSign:    false,
  }
}

// Validates a mapping. Returns null if ok, otherwise a string describing the
// problem in a user-friendly way. Used to disable the Save button in the UI
// until the mapping is complete.
export function validateMapping(mapping) {
  if (mapping.dateColumn == null || mapping.dateColumn < 0) {
    return 'Pick a column for the transaction date.'
  }
  if (mapping.descriptionColumn == null || mapping.descriptionColumn < 0) {
    return 'Pick a column for the description.'
  }
  if (mapping.amountMode === 'single') {
    if (mapping.amountColumn == null || mapping.amountColumn < 0) {
      return 'Pick a column for the amount, or switch to separate money-in / money-out columns.'
    }
  } else {
    // split: at least one of the two must be set (some CSVs only show one
    // direction in a column, which is unusual but possible)
    const noIn  = mapping.moneyInColumn  == null || mapping.moneyInColumn  < 0
    const noOut = mapping.moneyOutColumn == null || mapping.moneyOutColumn < 0
    if (noIn && noOut) {
      return 'Pick at least one of money-in or money-out columns.'
    }
  }
  return null
}

// ─── Display helpers ──────────────────────────────────────────────────────────

export function formatCurrency(amount) {
  if (amount === null || amount === undefined) return '—'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(amount)
}

export function formatDate(dateStr) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  if (!isNaN(d)) {
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  }
  return dateStr
}
